import { useEffect, useMemo, useRef } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { getOperatorById } from './operator.api';
import type { OperatorView } from './operator.types';

interface UseManagersOptions {
  ids: string[];
}

interface UseManagersReturn {
  managers: OperatorView[];
  isLoading: boolean;
  error: Error | undefined;
}

export function useManagers({ ids }: UseManagersOptions): UseManagersReturn {
  const { mutate } = useSWRConfig();
  const triggeredRef = useRef<string | null>(null);
  const uniqueIds = useMemo(() => Array.from(new Set(ids.filter((id) => id))), [ids]);

  const idsKey = uniqueIds.length > 0 ? [...uniqueIds].sort().join(',') : null;
  const swrKey = idsKey ? (['operators', idsKey] as const) : null;
  const {
    data: allManagers,
    isLoading,
    error,
  } = useSWR<OperatorView[]>(
    swrKey,
    uniqueIds.length > 0
      ? () =>
          Promise.allSettled(uniqueIds.map((id) => getOperatorById(id))).then((results) =>
            results
              .filter(
                (result): result is PromiseFulfilledResult<OperatorView> =>
                  result.status === 'fulfilled'
              )
              .map((result) => result.value)
          )
      : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: false,
      dedupingInterval: 3600000, // 1시간
    }
  );

  // revalidateOnMount: false일 때 초기 fetch 강제 (usePlanners와 동일)
  useEffect(() => {
    if (!swrKey || allManagers !== undefined || isLoading || error) return;
    if (triggeredRef.current === idsKey) return;
    triggeredRef.current = idsKey;
    mutate(swrKey);
  }, [swrKey, idsKey, allManagers, isLoading, error, mutate]);

  return {
    managers: allManagers || [],
    isLoading,
    error: error ? new Error(error.message || '매니저 로드 실패') : undefined,
  };
}
